import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import Breadcrumb from 'shopper/components/Breadcrumb/V1/Breadcrumb';

import BreadcrumbItem from 'components/Breadcrumb/Item';
import FrequentAskedQuestions from 'components/FrequentAskedQuestions';
import CategoriesCarousel from 'components/Landings/CategoriesCarousel';
import FeaturedImage from 'components/Landings/FeaturedImage';
import Layout from 'components/Layout';
import LazyHydration from 'components/LazyHydration';
import PageDescription from 'components/PageDescription';

import { getCategoriesLandings } from 'lib/landings';
import { toLandingPageLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { offerQueryKeys } from 'lib/queryKeys';

import { getLandingOffersList } from 'services/landing';

import Heading from './Heading';

import LINKS from 'constants/links';

const BlackFridayGlossary = dynamic(() => import('./BlackFridayGlossary'));
const OffersList = dynamic(() => import('components/Timeline/OffersList'));

const BlackFridayLanding = ({
  initialOffers = { offers: [] },
  offersListProps = {},
  categoryOrSubcategorySlug = {},
  landing: {
    landingId,
    landingCategories,
    landingDescription,
    landingEventCategory,
    landingFaqs,
    landingImage,
    landingMetaDescription,
    landingMetaTitle,
    landingName,
    landingSlug,
    landingText,
    landingTitle,
  },
  sort,
}) => {
  const { asPath } = useRouter();

  const gaEventCategory = landingEventCategory || landingName;

  const hasLandingCategories = useMemo(() => {
    return (
      landingCategories?.categories?.length > 0 ||
      landingCategories?.subcategories?.length > 0
    );
  }, [landingCategories]);

  const homeLandingItem = {
    id: landingId,
    name: landingName,
    slug: landingSlug,
    href: toLandingPageLink(landingSlug),
    active: asPath === '/black-friday/',
  };

  const orderedCategories = useMemo(
    () =>
      getCategoriesLandings(
        landingCategories,
        asPath,
        landingSlug,
        homeLandingItem
      ),
    [asPath]
  );

  return (
    <>
      <NextSeo description={landingMetaDescription} title={landingMetaTitle} />
      <Layout withGridLayout={false}>
        <Breadcrumb className="pb-0">
          <BreadcrumbItem href={LINKS.HOME}>
            {placeholder('breadcrumbs.app')}
          </BreadcrumbItem>
          <BreadcrumbItem>{landingName}</BreadcrumbItem>
        </Breadcrumb>
        <div className="flex flex-col">
          <Heading
            className="order-0 mt-0 lg:order-1 lg:mt-6"
            description={landingDescription}
            isDescriptionInitiallyCollapsed={initialOffers.offers.length > 0}
            size="size1"
            title={landingTitle}
          />
          {(landingImage.image || landingImage.imageMobile) && (
            <FeaturedImage
              bgcolor={landingImage.imageBgColors}
              className="overflow-hidden rounded-4"
              description={landingName}
              gaEventCategory={gaEventCategory}
              image={landingImage.image}
              mobileImage={landingImage.imageMobile}
              url={landingImage.imageLongUrl}
            />
          )}
        </div>
        {hasLandingCategories && (
          <CategoriesCarousel
            gaEventCategory={gaEventCategory}
            orderedCategories={orderedCategories}
          />
        )}
        {initialOffers.offers.length > 0 && (
          <OffersList
            className="md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            gaEventCategory={gaEventCategory}
            initialOffers={initialOffers}
            queryFn={({ after }) =>
              getLandingOffersList({
                after,
                landingSlug,
                sort,
              })
            }
            queryKey={offerQueryKeys.listByLanding({
              landingId,
              categoryOrSubcategorySlug,
            })}
            paginationCappedAtFinishedOffer
            {...offersListProps}
          />
        )}
        {landingText && (
          <LazyHydration placeholderSize={{ height: 300 }}>
            <PageDescription
              className="bg-transparent dark:bg-transparent"
              pageDescription={landingText}
            />
          </LazyHydration>
        )}
        {landingFaqs?.length > 0 && (
          <FrequentAskedQuestions className="mt-6" faqs={landingFaqs} />
        )}
        <BlackFridayGlossary className="mt-10" />
      </Layout>
    </>
  );
};

BlackFridayLanding.propTypes = {
  initialOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  offersListProps: PropTypes.shape(),
  landing: PropTypes.shape({
    landingCategories: PropTypes.shape({
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          categoryId: PropTypes.number.isRequired,
          categoryName: PropTypes.string.isRequired,
          categorySlug: PropTypes.string.isRequired,
        })
      ),
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          subcategoryId: PropTypes.number.isRequired,
          subcategoryName: PropTypes.string.isRequired,
          subcategorySlug: PropTypes.string.isRequired,
        })
      ),
    }),
    landingDescription: PropTypes.string.isRequired,
    landingEventCategory: PropTypes.string,
    landingFaqs: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string.isRequired,
        answer: PropTypes.string.isRequired,
      })
    ),
    landingImage: PropTypes.shape({
      image: PropTypes.string,
      imageMobile: PropTypes.string,
      imageBgColors: PropTypes.arrayOf(PropTypes.string),
      imageUrl: PropTypes.string,
      imageLongUrl: PropTypes.string,
    }),
    landingName: PropTypes.string.isRequired,
    landingMetaDescription: PropTypes.string.isRequired,
    landingMetaTitle: PropTypes.string.isRequired,
    landingSlug: PropTypes.string.isRequired,
    landingText: PropTypes.string.isRequired,
    landingTitle: PropTypes.string.isRequired,
    landingType: PropTypes.string.isRequired,
  }).isRequired,
  storesWithFeaturedCoupons: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string,
      storeSlug: PropTypes.string,
    }).isRequired
  ),
};

export default BlackFridayLanding;
